import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  MapPin, Briefcase, DollarSign, Clock, Building2,
  Share2, Bookmark, CheckCircle, Info, Calendar,
  Users, Award
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JobDetail = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [listings, setListings] = useState([]); // raw listings
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    fetchJob();
  }, [jobId]);

  useEffect(() => {
    fetchListings();
  }, []);

  const fetchJob = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/jobs/${jobId}`);
      setJob(response.data);
    } catch (error) {
      toast.error('Job not found');
      navigate('/jobs/browse');
    } finally {
      setLoading(false);
    }
  };

  const fetchListings = async () => {
    try {
      const response = await axios.get(`${API}/jobs/listings?limit=50`);
      setListings(response.data.jobs || []);
    } catch (error) {
      console.error('Failed to fetch job listings', error);
    }
  };

  const handleApply = () => {
    if (!user) {
      toast.error('Please login to apply');
      navigate('/jobs/login');
      return;
    }
    navigate(`/jobs/${jobId}/apply`);
  };

  const handleSave = () => {
    setSaved((v) => !v);
    toast.success(!saved ? 'Job saved!' : 'Job removed from saved');
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copied to clipboard');
  };

  const getEmploymentBadge = (type) => {
    const config = {
      'Full-time': { label: 'Full-time', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' },
      'Part-time': { label: 'Part-time', color: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' },
      'Casual': { label: 'Casual', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300' },
      'Contract': { label: 'Contract', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300' },
      'Internship': { label: 'Internship', color: 'bg-pink-100 text-pink-700 dark:bg-pink-900 dark:text-pink-300' },
      'Temporary': { label: 'Temporary', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300' }
    };

    const key = type;
    const typeConfig = config[key] || { label: type || 'Not specified', color: 'bg-gray-100 text-gray-700 dark:bg-gray-900 dark:text-gray-200' };

    // Return Badge element (tanpa Badge dobel)
    return <Badge className={typeConfig.color}>{typeConfig.label}</Badge>;
  };

  const formatSalary = (min, max, unit) => {
    if (!min || !max) return 'Salary not specified';
    if (unit === 'hour') return `$${min}-$${max}/hr`;
    return `$${min}K-$${max}K/year`;
  };

  const getWorkArrangement = (type = 'on_site') => {
    const arrangements = { on_site: 'On-site', hybrid: 'Hybrid', remote: 'Remote' };
    return arrangements[type] || 'On-site';
  };

  const getDaysUntilExpiry = (expiryDate) => {
    if (!expiryDate) return null;
    const days = Math.ceil((new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
    return days > 0 ? days : 0;
  };

  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  const hasCapacityLeft = (j) => {
    if (!j?.applicant_cap) return true;
    return (j.applications_count || 0) < j.applicant_cap;
  };

  // =========================
  // Similar Jobs (REAL filter)
  // =========================
  const similarJobs = useMemo(() => {
    if (!job || !Array.isArray(listings) || listings.length === 0) return [];

    const targetCategory = (job.role_category || '').toLowerCase();
    const targetState = (job.location_state || '').toLowerCase();
    const targetEmployment = (job.employment_type || '').toLowerCase();
    const targetArrangement = (job.work_arrangement || '').toLowerCase();

    const pool = listings.filter((j) => j && j.id !== job.id);

    // score matching
    const scored = pool.map((j) => {
      let score = 0;

      if ((j.role_category || '').toLowerCase() === targetCategory && targetCategory) score += 50;
      if ((j.location_state || '').toLowerCase() === targetState && targetState) score += 20;
      if ((j.employment_type || '').toLowerCase() === targetEmployment && targetEmployment) score += 15;
      if ((j.work_arrangement || '').toLowerCase() === targetArrangement && targetArrangement) score += 10;

      // kecilin noise: kalau suburb sama, bonus
      if ((j.location_suburb || '').toLowerCase() === (job.location_suburb || '').toLowerCase() && job.location_suburb) {
        score += 5;
      }

      return { ...j, _score: score };
    });

    // prefer yang skor tinggi, fallback kalau skor semua 0 tetap tampilkan
    const anyPositive = scored.some((x) => x._score > 0);

    const finalList = (anyPositive ? scored.filter((x) => x._score > 0) : scored)
      .sort((a, b) => (b._score || 0) - (a._score || 0))
      .slice(0, 6);

    return finalList;
  }, [job, listings]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-muted rounded w-3/4"></div>
            <div className="h-4 bg-muted rounded w-1/2"></div>
            <div className="h-64 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!job) return null;

  const expired = isExpired(job.expires_at);
  const capacityLeft = hasCapacityLeft(job);
  const canApply = !expired && capacityLeft;
  const daysLeft = getDaysUntilExpiry(job.expires_at);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/jobs/browse')}
          className="mb-6 flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="font-medium">Back to Jobs</span>
        </button>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content - 2/3 */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header */}
            <div>
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  {job.is_boosted && (
                    <Badge className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white border-0 mb-3">
                      ⚡ Boosted Job
                    </Badge>
                  )}
                  <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-3">
                    {job.title || job.job_title}
                  </h1>

                  <div className="flex items-center gap-3 flex-wrap mb-3">
                    <span className="text-sm text-muted-foreground">Job ID:</span>
                    <code className="px-3 py-1.5 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg text-sm font-bold text-blue-600 dark:text-blue-400 tracking-wider">
                      {job.job_code || job.id}
                    </code>
                  </div>

                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Building2 className="w-4 h-4" />
                      <span className="font-semibold">
                        {job.employer?.company_name || job.company_name || 'Company'}
                      </span>
                    </div>
                    <span className="text-muted-foreground">•</span>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <MapPin className="w-4 h-4" />
                      <span>{job.location_suburb}, {job.location_state}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Badges */}
              <div className="flex flex-wrap gap-2 mb-4">
                {getEmploymentBadge(job.employment_type)}
                <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                  {getWorkArrangement(job.work_arrangement)}
                </Badge>
                {job.employer?.abn_verified && (
                  <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    ABN Verified
                  </Badge>
                )}
              </div>

              {/* Meta Info */}
              <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>Posted {new Date(job.posted_at).toLocaleDateString()}</span>
                </div>
                {daysLeft !== null && daysLeft > 0 && (
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>Closes in {daysLeft} day{daysLeft !== 1 ? 's' : ''}</span>
                  </div>
                )}
                {job.applications_count > 0 && (
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    <span>{job.applications_count} application{job.applications_count !== 1 ? 's' : ''}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Job Snapshot */}
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold text-lg mb-4 text-foreground">Job Snapshot</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {job.salary_min && job.salary_max && (
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">Pay Range</div>
                      <div className="font-semibold text-foreground">
                        {formatSalary(job.salary_min, job.salary_max, job.salary_unit)}
                      </div>
                    </div>
                  )}
                  <div>
                    <div className="text-sm text-muted-foreground mb-1">Work Type</div>
                    <div className="font-semibold text-foreground capitalize">
                      {job.employment_type || 'Not specified'}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground mb-1">Location</div>
                    <div className="font-semibold text-foreground">
                      {getWorkArrangement(job.work_arrangement)}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground mb-1">Category</div>
                    <div className="font-semibold text-foreground">{job.role_category}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground mb-1">Job Ref</div>
                    <code className="font-mono text-sm font-semibold text-foreground">{job.public_ref}</code>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Job Description */}
            <Card>
              <CardContent className="p-6 space-y-6">
                <div>
                  <h3 className="font-semibold text-lg mb-3 text-foreground">About the Role</h3>
                  <div className="text-muted-foreground whitespace-pre-line leading-relaxed">
                    {job.description}
                  </div>
                </div>

                {job.responsibilities && job.responsibilities.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-lg mb-3 text-foreground">Key Responsibilities</h3>
                    <ul className="space-y-2">
                      {job.responsibilities.map((item, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                          <span className="text-muted-foreground">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {job.requirements && job.requirements.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-lg mb-3 text-foreground">Requirements</h3>
                    <ul className="space-y-2">
                      {job.requirements.map((item, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                          <span className="text-muted-foreground">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {job.benefits && job.benefits.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-lg mb-3 text-foreground">Benefits</h3>
                    <ul className="space-y-2">
                      {job.benefits.map((item, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <Award className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                          <span className="text-muted-foreground">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Similar Jobs */}
            {similarJobs.length > 0 && (
              <div>
                <h3 className="font-semibold text-xl mb-4 text-foreground">Similar Jobs</h3>

                <div className="grid md:grid-cols-2 gap-4">
                  {similarJobs.slice(0, 4).map((similarJob) => (
                    <Card
                      key={similarJob.id}
                      className="cursor-pointer hover:shadow-lg transition-all"
                      onClick={() => navigate(`/jobs/${similarJob.id}`)} // ✅ FIX: pakai ID agar cocok dengan /jobs/:jobId
                    >
                      <CardContent className="p-4">
                        <h4 className="font-semibold text-foreground mb-2 line-clamp-2">
                          {similarJob.title || similarJob.job_title}
                        </h4>
                        <div className="text-sm text-muted-foreground mb-2">
                          {similarJob.employer?.company_name || similarJob.company_name || 'Company'}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <MapPin className="w-3 h-3" />
                          <span>{similarJob.location_suburb}, {similarJob.location_state}</span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - 1/3 */}
          <div className="lg:col-span-1">
            <div className="sticky top-20 space-y-4">
              {/* Apply Card */}
              <Card>
                <CardContent className="p-6 space-y-4">
                  {job.salary_min && job.salary_max && (
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">Salary Range</div>
                      <div className="text-2xl font-bold text-foreground">
                        {formatSalary(job.salary_min, job.salary_max, job.salary_unit)}
                      </div>
                    </div>
                  )}

                  {/* Status Messages */}
                  {expired && (
                    <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                      <p className="text-sm font-semibold text-red-800 dark:text-red-200">
                        This job listing has expired
                      </p>
                      <p className="text-xs text-red-600 dark:text-red-300 mt-1">
                        Applications are no longer being accepted
                      </p>
                    </div>
                  )}

                  {!expired && !capacityLeft && (
                    <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                      <p className="text-sm font-semibold text-yellow-800 dark:text-yellow-200">
                        Application limit reached
                      </p>
                      <p className="text-xs text-yellow-600 dark:text-yellow-300 mt-1">
                        This position is no longer accepting applications
                      </p>
                    </div>
                  )}

                  {/* Apply Button */}
                  <button
                    onClick={handleApply}
                    disabled={!canApply}
                    className="w-full bg-purple-600 text-white font-semibold py-3 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <Briefcase className="w-5 h-5" />
                    {canApply ? 'Apply Now' : 'Applications Closed'}
                  </button>

                  {/* Secondary Actions */}
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={handleSave}
                      className="flex items-center justify-center gap-2 px-4 py-2 border border-border rounded-lg hover:bg-muted transition-colors"
                    >
                      <Bookmark className={`w-4 h-4 ${saved ? 'fill-current text-purple-600' : ''}`} />
                      <span className="text-sm">Save</span>
                    </button>
                    <button
                      onClick={handleShare}
                      className="flex items-center justify-center gap-2 px-4 py-2 border border-border rounded-lg hover:bg-muted transition-colors"
                    >
                      <Share2 className="w-4 h-4" />
                      <span className="text-sm">Share</span>
                    </button>
                  </div>
                </CardContent>
              </Card>

              {/* Company Info */}
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-lg mb-4 text-foreground">About the Company</h3>
                  <div className="space-y-3">
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">Company</div>
                      <div className="font-semibold text-foreground">{job.employer?.company_name}</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">Industry</div>
                      <div className="font-semibold text-foreground">{job.employer?.industry || job.role_category}</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">Company Size</div>
                      <div className="font-semibold text-foreground">{job.employer?.company_size || 'Not specified'}</div>
                    </div>

                    {/* Verification Badges */}
                    <div className="pt-3 border-t border-border">
                      <div className="text-sm text-muted-foreground mb-2 flex items-center gap-1">
                        Verification
                        <div className="group relative">
                          <Info className="w-3 h-3 cursor-help" />
                          <div className="hidden group-hover:block absolute left-0 bottom-full mb-2 w-64 p-3 bg-card border border-border rounded-lg shadow-lg text-xs z-10">
                            <p className="font-semibold mb-1">Verification Info</p>
                            <p className="text-muted-foreground">
                              ABN (Australian Business Number) and ACN (Australian Company Number) verification helps ensure legitimate employers.
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-1">
                        {job.employer?.abn && (
                          <div className="flex items-center gap-2">
                            <CheckCircle className={`w-4 h-4 ${job.employer?.abn_verified ? 'text-green-600' : 'text-gray-400'}`} />
                            <span className="text-sm">ABN {job.employer?.abn_verified ? 'Verified' : 'Provided'}</span>
                          </div>
                        )}
                        {job.employer?.acn && (
                          <div className="flex items-center gap-2">
                            <CheckCircle className={`w-4 h-4 ${job.employer?.acn_verified ? 'text-green-600' : 'text-gray-400'}`} />
                            <span className="text-sm">ACN {job.employer?.acn_verified ? 'Verified' : 'Provided'}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {/* Mobile Sticky Apply Bar */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-card border-t border-border p-4 z-40">
          <button
            onClick={handleApply}
            disabled={!canApply}
            className="w-full bg-purple-600 text-white font-semibold py-3 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
          >
            {canApply ? 'Apply Now' : 'Applications Closed'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default JobDetail;
